/**
 * O link do campo que já "assentou": a pessoa parou de mexer nele (27/10/2026).
 *
 * A barra em cima do campo só pode pedir o cartão de um link COMPLETO. Digitar `https://g1.globo.com/a` tecla
 * a tecla passa por `https://g`, `https://g1`... e cada um já é um link para a regra do texto; pedir o cartão de
 * cada um seria uma busca por tecla (spec §3.4). Por isso o link só assenta quando:
 *
 *  - ganhou algo depois dele: o espaço ou o Enter logo após a URL (a pessoa seguiu em frente);
 *  - o texto cresceu mais de 1 caractere entre dois renders (colar);
 *  - o hook nasce com o link já no texto (rascunho restaurado);
 *  - ou passaram 600 ms sem tecla.
 *
 * 🔴 Só o ESPAÇO (ou o Enter) fecha o link, não qualquer coisa depois dele. O `terminado` do `PrimeiroLink`
 * também vale para a pontuação aparada: em `https://g1.` o link é `https://g1` e já está "terminado" pelo ponto,
 * mas o ponto é do endereço que ainda vem (`g1.globo.com`), não do fim da frase. Valer só o `terminado` pediria o
 * cartão de `https://g1`, `https://g1.globo`... a cada ponto digitado. O branco tem de ter sido DIGITADO agora, no
 * fim do texto que já havia.
 *
 * Editar no meio de um link já terminado (trocar uma letra, apagar outra) NÃO é imediato: o link muda, mas nada
 * foi acrescentado depois dele, e a pessoa pode estar no meio da edição. Espera os 600 ms como qualquer tecla.
 *
 * Link que SOME do texto vira `null` na hora (a barra sai junto). Link TROCADO mantém o anterior até o novo
 * assentar; voltar ao anterior antes disso cancela a troca.
 *
 * `extrair` é o que tira o primeiro link do texto: `primeiroLink` no texto puro (chat, comentário, atendimento) e
 * `primeiroLinkDoHtml` no post do feed. Tem de ser estável entre renders (função de módulo): uma nova a cada
 * render recomeçaria a contagem da pausa.
 *
 * A decisão acontece NO render (estado derivado do render anterior, o padrão do React para isto), não num efeito:
 * assim o valor devolvido já é o certo no mesmo render, sem um quadro com o link velho. O único efeito é o timer
 * dos 600 ms.
 */
import { useEffect, useMemo, useState } from "react";

import { primeiroLink, type PrimeiroLink } from "@/lib/links/links";

/** Quanto tempo sem tecla até o link assentar. */
export const PAUSA_DO_LINK_MS = 600;

interface Lembrado {
  /** O texto do render anterior: dele sai "cresceu mais de 1" e "o que foi acrescentado no fim". */
  texto: string;
  /** O link assentado: o que a barra mostra. */
  assentado: PrimeiroLink | null;
}

/** Colar: o texto cresceu mais de 1 caractere (uma tecla acrescenta 1). */
function foiColagem(anterior: string, texto: string): boolean {
  return texto.length - anterior.length > 1;
}

/**
 * Digitou-se um espaço ou Enter DEPOIS do link: o texto é o anterior com algo acrescentado no fim, esse algo tem
 * um branco, e o link está terminado. Branco no meio do texto (ou do próprio link) não conta.
 */
function fechouComBranco(anterior: string, texto: string, link: PrimeiroLink): boolean {
  if (!link.terminado || !texto.startsWith(anterior)) return false;
  return /\s/.test(texto.slice(anterior.length));
}

function mesmoEndereco(a: PrimeiroLink | null, b: PrimeiroLink | null): boolean {
  return a !== null && b !== null && a.href === b.href;
}

export function useLinkAssentado(
  texto: string,
  extrair: (texto: string) => PrimeiroLink | null = primeiroLink
): PrimeiroLink | null {
  const link = useMemo(() => extrair(texto), [texto, extrair]);
  const [lembrado, setLembrado] = useState<Lembrado>(() => ({ texto, assentado: link }));

  // O que está assentado neste render: o que ficou do anterior, ou o que o texto novo manda assentar já.
  let assentado = lembrado.assentado;
  if (lembrado.texto !== texto) {
    if (link === null) assentado = null;
    else if (mesmoEndereco(link, lembrado.assentado)) assentado = lembrado.assentado;
    else if (foiColagem(lembrado.texto, texto) || fechouComBranco(lembrado.texto, texto, link))
      assentado = link;
    setLembrado({ texto, assentado });
  }

  // Esperando: há um link no texto que ainda não é o assentado. 600 ms sem tecla e ele assenta; qualquer tecla
  // (outro texto, logo outro `link`) desfaz o timer e recomeça.
  const esperando = link !== null && !mesmoEndereco(link, assentado);
  useEffect(() => {
    if (!esperando || link === null) return;
    const timer = setTimeout(
      () => setLembrado((atual) => ({ texto: atual.texto, assentado: link })),
      PAUSA_DO_LINK_MS
    );
    return () => clearTimeout(timer);
  }, [esperando, link]);

  return link === null ? null : assentado;
}
